import { Box, Typography } from '@mui/material';
import React from 'react';

import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';

import { IEntityDetails, OnEntitySelectCallback, PRODUCT_KEY } from '../../types';
import { ContextSwitcherListItem } from '../AppSwitcher/ContextSwitcherListItem';

import { getInactiveEntities } from '../../Components/utils';
import { getApplicationByKey, getEntityDefaultApplication } from '../utils';

import { EntityLogo } from './EntityLogo';
import { navigateToEntityUrl } from './onEntitySelectUtils';

interface IDesktopEntitySwitcherListItemsProps {
  activeEntity: IEntityDetails;
  entitledEntities: IEntityDetails[];
  forcedDefaultApplication?: PRODUCT_KEY;
  firstContextSwitcherItemRef?: React.MutableRefObject<HTMLLIElement>;
  anchorEl?: HTMLDivElement;
  onEntitySelect?: OnEntitySelectCallback;
  handleEntitySelect(entity: IEntityDetails): void;
  handleSwitchEntitiesSelect(): void;
  handleClose?(): void;
}
export const DesktopEntitySwitcherListItems: React.FC<IDesktopEntitySwitcherListItemsProps> = ({
  activeEntity,
  entitledEntities,
  forcedDefaultApplication,
  firstContextSwitcherItemRef,
  anchorEl,
  onEntitySelect,
  handleEntitySelect,
  handleSwitchEntitiesSelect,
  handleClose
}) => {
  // Temporary placeholder until a url is returned from the access control API on entity objects
  const logoUrl = '';

  const firstFourInactiveEntities = getInactiveEntities({ entitledEntities, activeEntity, limit: 4 });

  return (
    <>
      <ContextSwitcherListItem
        tooltipText={''}
        anchorEl={anchorEl}
        active={true}
        styleOverrides={{ justifyContent: 'center' }}
        dataTest={getDynamicDataTestId(DATA_TEST.entity_switcher_selection_dialog_option, activeEntity.name)}
        dataOg={getDynamicDataOGId(DATA_OG.entity_switcher_selection_dialog_option, activeEntity.name)}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, maxWidth: '100%' }}>
          <EntityLogo customLogoUrl={logoUrl} entityName={activeEntity?.name} />
          <Typography variant="h5" component="span" noWrap={true}>
            {activeEntity?.displayName || activeEntity?.name}
          </Typography>
        </Box>
      </ContextSwitcherListItem>
      {firstFourInactiveEntities?.length > 0 && (
        <Typography component="li" variant="h5" aria-hidden="true" sx={{ px: 2, py: 1 }}>
          My Entities
        </Typography>
      )}
      {/* Only show the first 4 results */}
      {firstFourInactiveEntities?.map((entity, index) => {
        const defaultApplication = forcedDefaultApplication
          ? getApplicationByKey(entity, forcedDefaultApplication)
          : getEntityDefaultApplication(entity);

        const hasUrlOverride = !!onEntitySelect && !!defaultApplication?.url;
        return (
          <ContextSwitcherListItem
            tooltipText={`Switch to ${entity.displayName || entity.name}`}
            active={false}
            autoFocus={index === 0}
            firstContextSwitcherItemRef={firstContextSwitcherItemRef}
            anchorEl={anchorEl}
            url={hasUrlOverride ? undefined : defaultApplication?.url}
            onClick={() => {
              if (!defaultApplication?.url) {
                handleEntitySelect(entity);
                return;
              }
              if (!hasUrlOverride) return;
              navigateToEntityUrl(
                onEntitySelect,
                entity,
                defaultApplication.url,
                (finalUrl) => {
                  if (defaultApplication.openInNewTab) {
                    window.open(finalUrl, '_blank');
                  } else {
                    window.location.href = finalUrl;
                  }
                },
                () => handleClose?.()
              );
            }}
            openInNewTab={hasUrlOverride ? false : defaultApplication?.openInNewTab}
            key={entity.id}
            dataTest={getDynamicDataTestId(
              DATA_TEST.entity_switcher_selection_dialog_option,
              entity.displayName || entity.name
            )}
            dataOg={getDynamicDataOGId(
              DATA_OG.entity_switcher_selection_dialog_option,
              entity.displayName || entity.name
            )}
          >
            <Typography variant="bodyDefault" component="span" noWrap={true}>
              {entity.displayName || entity.name}
            </Typography>
          </ContextSwitcherListItem>
        );
      })}
      {entitledEntities?.length >= 6 && (
        <ContextSwitcherListItem
          tooltipText={`Switch Entities`}
          anchorEl={anchorEl}
          active={false}
          key="switch-entities"
          dataTest={DATA_TEST.entity_switcher_open_dialog_option}
          dataOg={DATA_OG.entity_switcher_open_dialog_option}
          onClick={handleSwitchEntitiesSelect}
          openInNewTab={false}
        >
          <Typography variant="bodyDefault" component="span">
            Switch Entities {'»'}
          </Typography>
        </ContextSwitcherListItem>
      )}
    </>
  );
};
